/**
 * @module CreateNotifications
 * @package @geenius/notifications-solidjs
 * @description Implements the primary inbox primitive for the SolidJS
 * notifications variant. It centralizes status and type filtering, unread
 * counts, and grouped timeline sections for page and panel components.
 */

import type {
  Notification,
  NotificationStatus,
  NotificationType,
} from '@geenius/notifications/shared'
import { groupByDate } from '@geenius/notifications/shared'
import type { Accessor, Setter } from 'solid-js'
import { createMemo, createSignal } from 'solid-js'

/**
 * Async handlers supported by the inbox primitive.
 */
export interface NotificationActionHandlers {
  markRead: (id: string) => Promise<void>
  markAllRead: () => Promise<void>
  dismiss: (id: string) => Promise<void>
  deleteNotification: (id: string) => Promise<void>
}

/**
 * Shape returned by {@link createNotifications}.
 */
export interface CreateNotificationsResult {
  notifications: Accessor<Notification[]>
  allNotifications: Accessor<Notification[]>
  unreadCount: Accessor<number>
  grouped: Accessor<Map<string, Notification[]>>
  statusFilter: Accessor<NotificationStatus | 'all'>
  setStatusFilter: Setter<NotificationStatus | 'all'>
  typeFilter: Accessor<NotificationType | 'all'>
  setTypeFilter: Setter<NotificationType | 'all'>
  isLoading: Accessor<boolean>
  markRead?: NotificationActionHandlers['markRead']
  markAllRead?: NotificationActionHandlers['markAllRead']
  dismiss?: NotificationActionHandlers['dismiss']
  deleteNotification?: NotificationActionHandlers['deleteNotification']
}

/**
 * Computes derived inbox state for the SolidJS notification views.
 *
 * @param notifications Source notification accessor, or `undefined` while loading.
 * @param actions Optional async mutation handlers for notification actions.
 * @returns Derived inbox state and mutation callbacks.
 */
export function createNotifications(
  notifications: Accessor<Notification[] | undefined>,
  actions?: Partial<NotificationActionHandlers>,
): CreateNotificationsResult {
  const [statusFilter, setStatusFilter] = createSignal<NotificationStatus | 'all'>('all')
  const [typeFilter, setTypeFilter] = createSignal<NotificationType | 'all'>('all')
  const isLoading = createMemo(() => notifications() === undefined)

  const filtered = createMemo(() => {
    let nextNotifications = notifications() ?? []
    const currentStatus = statusFilter()
    const currentType = typeFilter()

    if (currentStatus !== 'all') {
      nextNotifications = nextNotifications.filter(
        (notification) => notification.status === currentStatus,
      )
    }

    if (currentType !== 'all') {
      nextNotifications = nextNotifications.filter(
        (notification) => notification.type === currentType,
      )
    }

    return nextNotifications
  })

  const unreadCount = createMemo(
    () => (notifications() ?? []).filter((notification) => notification.status === 'unread').length,
  )
  const grouped = createMemo(() => groupByDate(filtered()))

  return {
    notifications: filtered,
    allNotifications: () => notifications() ?? [],
    unreadCount,
    grouped,
    statusFilter,
    setStatusFilter,
    typeFilter,
    setTypeFilter,
    isLoading,
    markRead: actions?.markRead,
    markAllRead: actions?.markAllRead,
    dismiss: actions?.dismiss,
    deleteNotification: actions?.deleteNotification,
  }
}
